import { useEffect, useRef } from 'react';

import { trackValidationFailed } from './uniqueIdTransfer';

// Records the moment an applicant is shown an error on a field.
//
// `showErrors` going true is exactly that moment — it is the flag every field
// component uses to render its error state. Watching the transition rather than
// the value means a field that stays in error across re-renders is recorded once,
// not on every render.
//
// The initial run records the value WITHOUT firing: a component that mounts
// with its error already showing is a remount, not a new failure. Some embeds
// remount screens constantly (observed in production: one partner's pages
// re-render the widget dozens of times a session), and firing on mount turned
// each remount into a phantom validation failure — inflating the error column
// by an order of magnitude.
//
// Without this hook the friction table's error column was permanently zero,
// which removed the only signal separating "this question is confusing" from
// "this box will not accept what they typed" — two problems with completely
// different fixes.
export function useFieldValidation(field: string | undefined, showErrors?: boolean): void {
    const wasShowing = useRef<boolean | undefined>(undefined);

    useEffect(() => {
        const showing = Boolean(showErrors);

        if (showing && wasShowing.current === false && field) {
            trackValidationFailed(field);
        }

        wasShowing.current = showing;
    }, [showErrors, field]);
}
